<template>
  <modal :show.sync="show">
    <h6 slot="header" class="modal-title" id="modal-title-branch-details">
      <i class="ni ni-building text-info mr-2"></i>Branch Details
    </h6>
    
    <template v-if="branch">
      <div class="branch-details-container">
        <!-- Branch Header -->
        <div class="branch-detail-header">
          <div class="branch-title">
            <h4>{{branch.name}}</h4>
            <span v-if="branch.is_main" class="main-branch-label">Main Branch</span>
          </div>
          <p v-if="branch.name_ar" class="arabic-name">{{branch.name_ar}}</p>
        </div>
        
        <!-- Branch Details Sections -->
        <div class="branch-detail-section">
          <div class="section-icon">
            <i class="ni ni-pin-3 text-danger"></i>
          </div>
          <div class="section-content">
            <h6>Location</h6>
            <div v-if="branch.address" class="detail-item">
              <span class="detail-label">Address:</span>
              <span class="detail-value">{{branch.address}}</span>
            </div>
            <div v-if="branch.address_ar" class="detail-item">
              <span class="detail-label">Address (AR):</span>
              <span class="detail-value">{{branch.address_ar}}</span>
            </div>
            <div v-if="branch.city" class="detail-item">
              <span class="detail-label">City:</span>
              <span class="detail-value">{{branch.city}}</span>
            </div>
            <div v-if="branch.city_ar" class="detail-item">
              <span class="detail-label">City (AR):</span>
              <span class="detail-value">{{branch.city_ar}}</span>
            </div>
            <div v-if="!branch.address && !branch.address_ar && !branch.city" class="detail-no-data">
              No location information provided
            </div>
          </div>
        </div>
        
        <!-- Contact Section -->
        <div class="branch-detail-section">
          <div class="section-icon">
            <i class="ni ni-mobile-button text-primary"></i>
          </div>
          <div class="section-content">
            <h6>Contact</h6>
            <div v-if="branch.phone" class="detail-item">
              <span class="detail-label">Phone:</span>
              <span class="detail-value">{{branch.phone}}</span>
            </div>
            <div v-if="!branch.phone" class="detail-no-data">
              No contact information provided
            </div>
          </div>
        </div>
        
        <!-- GPS Coordinates Section -->
        <div v-if="branch.location_lat && branch.location_long" class="branch-detail-section">
          <div class="section-icon">
            <i class="ni ni-map-big text-success"></i>
          </div>
          <div class="section-content">
            <h6>GPS Coordinates</h6>
            <div class="coords-box">
              <div class="coord-item">
                <span class="coord-label">Latitude:</span>
                <span class="coord-value">{{branch.location_lat}}</span>
              </div>
              <div class="coord-item">
                <span class="coord-label">Longitude:</span>
                <span class="coord-value">{{branch.location_long}}</span>
              </div>
              <button class="btn btn-sm btn-outline-primary mt-2" @click="openMaps">
                <i class="fa fa-map-marker mr-1"></i> View on Google Maps
              </button>
            </div>
          </div>
        </div>
        
        <!-- Hours Section -->
        <div class="branch-detail-section">
          <div class="section-icon">
            <i class="ni ni-time-alarm text-warning"></i>
          </div>
          <div class="section-content">
            <h6>Hours</h6>
            <div class="hours-actions">
              <base-button 
                @click="$emit('view-business-hours', branch)" 
                type="outline-primary" 
                size="sm" 
                class="mr-2"
              >
                <i class="ni ni-time-alarm mr-1"></i> Business Hours
              </base-button>
              <base-button 
                @click="$emit('view-leisure-hours', branch)" 
                type="outline-warning" 
                size="sm"
              >
                <i class="ni ni-coffee-grain mr-1"></i> Break Hours
              </base-button>
            </div>
          </div>
        </div>
      </div>
    </template>
    
    <template slot="footer">
      <base-button type="default" @click="$emit('update:show', false)">
        <i class="fa fa-times mr-1"></i> Close
      </base-button>
      <base-button type="primary" @click="$emit('edit', branch)">
        <i class="fa fa-pencil mr-1"></i> Edit Branch
      </base-button>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import BaseButton from "@/components/BaseButton";

export default {
  name: 'BranchDetailsModal',
  components: {
    Modal,
    BaseButton
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    branch: {
      type: Object,
      default: null
    }
  },
  methods: {
    openMaps() {
      if (this.branch && this.branch.location_lat && this.branch.location_long) {
        const url = `https://www.google.com/maps/search/?api=1&query=${this.branch.location_lat},${this.branch.location_long}`;
        window.open(url, '_blank');
      }
    }
  }
}
</script>

<style scoped>
.branch-details-container {
  padding: 0;
}

.branch-detail-header {
  padding: 0 0 15px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 20px;
}

.branch-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.branch-title h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.main-branch-label {
  background-color: #5e72e4;
  color: white;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
}

.arabic-name {
  margin: 5px 0 0;
  font-size: 1rem;
  color: #8898aa;
  direction: rtl;
  text-align: right;
}

.branch-detail-section {
  display: flex;
  padding: 15px 0;
  border-bottom: 1px solid #f6f9fc;
}

.branch-detail-section:last-child {
  border-bottom: none;
}

.section-icon {
  width: 40px;
  height: 40px;
  background-color: #f6f9fc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.section-icon i {
  font-size: 1rem;
}

.section-content {
  flex: 1;
}

.section-content h6 {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 10px;
  color: #32325d;
}

.detail-item {
  margin-bottom: 8px;
  display: flex;
}

.detail-label {
  font-weight: 500;
  font-size: 0.8rem;
  color: #8898aa;
  width: 120px;
}

.detail-value {
  font-size: 0.875rem;
  color: #525f7f;
  flex: 1;
}

.detail-no-data {
  color: #8898aa;
  font-style: italic;
  font-size: 0.8rem;
}

.coords-box {
  background-color: #f6f9fc;
  border-radius: 0.375rem;
  padding: 10px 15px;
  margin-top: 5px;
}

.coord-item {
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.coord-label {
  font-weight: 500;
  color: #8898aa;
  width: 80px;
}

.coord-value {
  font-family: monospace;
  color: #525f7f;
}

.hours-actions {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
</style>
